import { z } from 'zod';
/**
 * Support (`product/business-rules.md`, Support): an Account User asks
 * Groundly for help from the dashboard, and Groundly's admins answer from the
 * admin panel. A request is a ticket with a thread of messages. Any Account
 * User can open one, and everyone in the account sees the account's tickets.
 */
export const SUPPORT_CATEGORIES = [
    'billing',
    'assistant_answers',
    'knowledge',
    'widget_setup',
    'account',
    'other',
];
export const supportCategorySchema = z.enum(SUPPORT_CATEGORIES);
export const SUPPORT_CATEGORY_LABELS = {
    billing: 'Billing and plans',
    assistant_answers: "The assistant's answers",
    knowledge: 'Knowledge and crawling',
    widget_setup: 'Installing the widget',
    account: 'Account and users',
    other: 'Something else',
};
/**
 * `open`: waiting for Groundly; `waiting_on_client`: Groundly replied and
 * waits for the client; `resolved`: done. A client's reply reopens it.
 */
export const SUPPORT_STATUSES = ['open', 'waiting_on_client', 'resolved'];
export const supportStatusSchema = z.enum(SUPPORT_STATUSES);
export const MAX_SUPPORT_SUBJECT_LENGTH = 150;
export const MAX_SUPPORT_MESSAGE_LENGTH = 5000;
export const SUPPORT_PAGE_SIZE = 25;
const page = z.coerce.number().int().min(1).default(1);
const ticketParams = z.object({ ticketId: z.uuid() });
const supportMessageContent = z
    .string()
    .trim()
    .min(1, 'Write a message.')
    .max(MAX_SUPPORT_MESSAGE_LENGTH, `Keep it under ${MAX_SUPPORT_MESSAGE_LENGTH} characters.`);
export const supportMessageSchema = z.object({
    id: z.uuid(),
    /** Who wrote it: someone in the client's account, or a Groundly admin. */
    authorKind: z.enum(['client', 'admin']),
    /** The client's name (or email), or "Groundly support" for an admin to the client. */
    authorName: z.string(),
    content: z.string(),
    createdAt: z.iso.datetime(),
});
export const supportTicketSummarySchema = z.object({
    id: z.uuid(),
    /** Shown as "#1042": short, and the same in the dashboard, the admin panel and emails. */
    number: z.number().int().min(1),
    subject: z.string(),
    category: supportCategorySchema,
    status: supportStatusSchema,
    websiteName: z.string().nullable(),
    /** Who opened it. */
    openedBy: z.string(),
    lastMessageAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
});
export const supportTicketSchema = supportTicketSummarySchema.extend({
    websiteId: z.uuid().nullable(),
    /** Oldest first. */
    messages: z.array(supportMessageSchema),
});
const ticketListResponse = z.object({
    items: z.array(supportTicketSummarySchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
});
// ---------------------------------------------------------------------------
// Dashboard: any Account User
// ---------------------------------------------------------------------------
export const openSupportTicketBodySchema = z.object({
    subject: z
        .string()
        .trim()
        .min(1, 'Add a subject.')
        .max(MAX_SUPPORT_SUBJECT_LENGTH, `Keep it under ${MAX_SUPPORT_SUBJECT_LENGTH} characters.`),
    category: supportCategorySchema,
    /** The website it is about, when it is about one. */
    websiteId: z.uuid().nullable(),
    message: supportMessageContent,
});
export const supportMessageBodySchema = z.object({ content: supportMessageContent });
/** The account's tickets: open and waiting first, then by latest message. */
export const listSupportTickets = {
    method: 'GET',
    path: '/api/dashboard/support/tickets',
    params: z.object({}),
    query: z.object({ page }),
    body: z.object({}),
    response: ticketListResponse,
};
export const openSupportTicket = {
    method: 'POST',
    path: '/api/dashboard/support/tickets',
    params: z.object({}),
    query: z.object({}),
    body: openSupportTicketBodySchema,
    response: supportTicketSchema,
};
export const getSupportTicket = {
    method: 'GET',
    path: '/api/dashboard/support/tickets/:ticketId',
    params: ticketParams,
    query: z.object({}),
    body: z.object({}),
    response: supportTicketSchema,
};
/** A reply from the client; it reopens a resolved or waiting ticket. */
export const replySupportTicket = {
    method: 'POST',
    path: '/api/dashboard/support/tickets/:ticketId/messages',
    params: ticketParams,
    query: z.object({}),
    body: supportMessageBodySchema,
    response: supportTicketSchema,
};
/** The client says their issue is sorted. */
export const resolveSupportTicket = {
    method: 'POST',
    path: '/api/dashboard/support/tickets/:ticketId/resolve',
    params: ticketParams,
    query: z.object({}),
    body: z.object({}),
    response: supportTicketSchema,
};
// ---------------------------------------------------------------------------
// Admin panel: Groundly admins
// ---------------------------------------------------------------------------
export const adminSupportTicketSummarySchema = supportTicketSummarySchema.extend({
    accountId: z.uuid(),
    /** The account's first super user's email: accounts have no name of their own. */
    accountOwnerEmail: z.string().nullable(),
    messageCount: z.number().int().min(1),
});
export const adminSupportTicketSchema = supportTicketSchema.extend({
    accountId: z.uuid(),
    /** The account's first super user's email: accounts have no name of their own. */
    accountOwnerEmail: z.string().nullable(),
    /** The email address of the Account User who opened it, for replying outside the app. */
    openedByEmail: z.string().nullable(),
});
/** Every account's tickets, filtered by status; open first, oldest waiting first. */
export const listAdminSupportTickets = {
    method: 'GET',
    path: '/api/admin/support/tickets',
    params: z.object({}),
    query: z.object({ status: supportStatusSchema.optional(), page }),
    body: z.object({}),
    response: z.object({
        items: z.array(adminSupportTicketSummarySchema),
        page: z.number().int().min(1),
        pageSize: z.number().int().min(1),
        total: z.number().int().min(0),
        /** Tickets waiting for Groundly, for the menu badge. */
        openCount: z.number().int().min(0),
    }),
};
export const getAdminSupportTicket = {
    method: 'GET',
    path: '/api/admin/support/tickets/:ticketId',
    params: ticketParams,
    query: z.object({}),
    body: z.object({}),
    response: adminSupportTicketSchema,
};
export const adminSupportReplyBodySchema = z.object({
    content: supportMessageContent,
    /** The status after the reply: usually waiting on the client, or resolved. */
    status: z.enum(['waiting_on_client', 'resolved']).default('waiting_on_client'),
});
/** A reply from Groundly; the client who opened the ticket is emailed. */
export const replyAdminSupportTicket = {
    method: 'POST',
    path: '/api/admin/support/tickets/:ticketId/messages',
    params: ticketParams,
    query: z.object({}),
    body: adminSupportReplyBodySchema,
    response: adminSupportTicketSchema,
};
export const setAdminSupportStatus = {
    method: 'POST',
    path: '/api/admin/support/tickets/:ticketId/status',
    params: ticketParams,
    query: z.object({}),
    body: z.object({ status: supportStatusSchema }),
    response: adminSupportTicketSchema,
};
//# sourceMappingURL=support.js.map